import { useId, useRef, useState, type FormEvent } from 'react';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { exportErrorMessage } from '../exportErrors';
import { DIMENSION_LABEL } from '../exportOptions';
import { CATEGORIES, categoryLabel, categorySide, type AccountingMapping, type ChartAccount, type MappingTreatment, type WageGroupDimension } from '../exportTypes';
import { accountingGateway } from '../index';

export interface MappingPrefill {
  category?: string;
  subcategory?: string;
  payrollCode?: string;
  effectiveFrom?: string;
  /** A wage mapping for one group (category EarningsGross): the group is fixed and shown, not chosen. */
  groupDimension?: WageGroupDimension;
  groupKey?: string;
  groupLabel?: string;
  /** The category (and payroll code) are fixed: the mapping is for one deduction, earning or benefit code. */
  lockCategory?: boolean;
  /** A readable name for what is being mapped (a group or a code), shown above the form. */
  subject?: string;
}

export interface PickerOption {
  id: string;
  label: string;
}

const REASON_MAX = 300;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The guidance the contract gives per category (a different account type is only a warning, never a block). */
function accountTypeAdvice(category: string, accountType: string | undefined): string | null {
  if (!accountType) return null;
  const side = categorySide(category);
  if (side === 'Debit' && accountType !== 'Expense') return `An expense account is usual for ${categoryLabel(category).toLowerCase()}; a ${accountType.toLowerCase()} account is allowed but will be flagged on every preview.`;
  if (category === 'NetPayClearing' && accountType === 'Asset') return null;
  if (category === 'NetPayClearing' && accountType !== 'Clearing' && accountType !== 'Liability') return 'Net pay should post to a Clearing or Liability account.';
  if (side === 'Credit' && accountType === 'Expense') return 'A liability account is usual for this category; an expense account will be flagged on every preview.';
  return null;
}

/**
 * Add a GL mapping. A mapping is never edited in place: saving creates a NEW version, and ending the old one keeps the history. The dialog validates
 * what it can before sending (and says so in an error summary that takes focus), but the server decides: its refusal is shown inside the dialog.
 */
export function MappingDialog({ prefill, accounts, employees, departments, replaces, title, onClose, onSaved }: {
  prefill?: MappingPrefill;
  accounts: ChartAccount[];
  employees: PickerOption[] | null;
  departments: PickerOption[] | null;
  /** An active mapping this one replaces: it is ended first (history is kept), then the new version is created. */
  replaces?: AccountingMapping | null;
  title?: string;
  onClose: () => void;
  onSaved: (mapping: AccountingMapping) => void;
}) {
  const uid = useId();
  const summaryRef = useRef<HTMLDivElement>(null);
  const grouped = !!(prefill?.groupDimension && prefill.groupKey);
  const lockedCategory = grouped || prefill?.lockCategory === true;
  const [category, setCategory] = useState(grouped ? 'EarningsGross' : (prefill?.category ?? ''));
  const [subcategory, setSubcategory] = useState(prefill?.subcategory ?? '');
  const [payrollCode, setPayrollCode] = useState(prefill?.payrollCode ?? '');
  const [employeeId, setEmployeeId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [treatment, setTreatment] = useState<MappingTreatment>('Post');
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  // What the person typed; until they type, the suggested date (which can arrive after the dialog opens) is shown.
  const [typedDate, setTypedDate] = useState<string | null>(null);
  const effectiveFrom = typedDate ?? prefill?.effectiveFrom ?? '';
  const [confirmFunding, setConfirmFunding] = useState(false);
  const [issues, setIssues] = useState<SummaryIssue[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const id = (key: string) => `${uid}-${key}`;
  const account = accounts.find((a) => a.id === accountId);
  const needsFundingConfirmation = treatment === 'Post' && category === 'NetPayClearing' && account?.accountType === 'Asset';
  const side = categorySide(category);
  const advice = treatment === 'Post' ? accountTypeAdvice(category, account?.accountType) : null;

  function focusField(key: string) {
    document.getElementById(id(key))?.focus();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    const found: SummaryIssue[] = [];
    if (!category) found.push({ key: 'category', message: 'Choose a payroll category.' });
    if (treatment === 'Post' && !accountId) found.push({ key: 'account', message: 'Choose the account this category posts to.' });
    if (treatment === 'NonPosting' && !reason.trim()) found.push({ key: 'reason', message: 'Enter the reason this item is not posted.' });
    if (reason.trim().length > REASON_MAX) found.push({ key: 'reason', message: `Keep the reason to ${REASON_MAX} characters or fewer.` });
    if (!DATE_PATTERN.test(effectiveFrom)) found.push({ key: 'effectiveFrom', message: 'Enter the date this mapping takes effect.' });
    if (needsFundingConfirmation && !confirmFunding) found.push({ key: 'funding', message: 'Confirm that net pay should post to a bank account.' });
    setIssues(found);
    if (found.length > 0) {
      queueMicrotask(() => summaryRef.current?.focus());
      return;
    }
    setBusy(true);
    try {
      // Replacing: the old version is ended first (it stays as history, and exports already generated never change), then the new one is added.
      if (replaces) await accountingGateway.endMapping(replaces.id);
      const saved = await accountingGateway.createMapping({
        category, treatment, effectiveFrom,
        ...(subcategory.trim() ? { subcategory: subcategory.trim() } : {}),
        ...(payrollCode.trim() ? { payrollCode: payrollCode.trim() } : {}),
        ...(employeeId ? { employeeId } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(locationCode.trim() ? { locationCode: locationCode.trim() } : {}),
        ...(grouped && prefill?.groupDimension && prefill.groupKey ? { groupDimension: prefill.groupDimension, groupKey: prefill.groupKey } : {}),
        ...(treatment === 'NonPosting' ? { nonPostingReason: reason.trim() } : { chartAccountId: accountId }),
        ...(needsFundingConfirmation ? { confirmedFundingModel: true } : {}),
      });
      onSaved(saved);
    } catch (err) {
      setServerError(exportErrorMessage(err, 'The mapping could not be saved.'));
      setBusy(false);
    }
  }

  const has = (key: string) => issues.some((i) => i.key === key);
  const invalid = (key: string) => (has(key) ? { 'aria-invalid': true as const, 'aria-describedby': `${id(key)}-err` } : {});
  const err = (key: string) => issues.filter((i) => i.key === key).map((i) => <p key={i.message} id={`${id(key)}-err`} className="jr-error">{i.message}</p>);

  return (
    <ModalDialog
      title={title ?? 'Add GL mapping'}
      size="lg"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={id('form')} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save mapping'}</button>
        </>
      )}
    >
      <form id={id('form')} onSubmit={(e) => void submit(e)} noValidate className="ax-form">
        <p className="jr-hint">
          Saving creates a new mapping version. Older mappings are kept as history and exports that were already generated never change.
          The most specific mapping wins: employee, then payroll code, then department or location, then the company default.
        </p>
        {prefill?.subject && <p data-testid="mapping-subject"><strong>{prefill.subject}</strong></p>}
        {replaces && (
          <p className="jr-warning" role="note" data-testid="mapping-replaces">
            This replaces the current mapping to {replaces.accountNumber ?? 'no account'} (version {replaces.mappingVersion}). The current version is ended and kept as history; exports already generated do not change.
          </p>
        )}
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
        {serverError && <p className="jr-error jr-error--block" role="alert">{serverError}</p>}
        <div className="jr-grid">
          <div className="jr-field">
            <label htmlFor={id('category')}>Payroll category <span aria-hidden="true" className="jr-req">*</span></label>
            <select id={id('category')} value={category} onChange={(e) => setCategory(e.target.value)} required disabled={lockedCategory} {...(lockedCategory ? {} : { 'data-autofocus': true })} {...invalid('category')}>
              <option value="">Choose a category</option>
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label} ({c.side.toLowerCase()})</option>)}
            </select>
            {side && <p className="jr-hint">This category posts on the {side.toLowerCase()} side. {CATEGORIES.find((c) => c.key === category)?.hint} is usual.</p>}
            {err('category')}
          </div>
          <div className="jr-field">
            <label htmlFor={id('subcategory')}>Subcategory (optional)</label>
            <input id={id('subcategory')} value={subcategory} onChange={(e) => setSubcategory(e.target.value)} aria-describedby={id('subcategory-hint')} maxLength={80} disabled={grouped} />
            <p id={id('subcategory-hint')} className="jr-hint">For example SocialSecurity or StateIncomeTax. Leave empty to cover every subcategory.</p>
          </div>
          <div className="jr-field">
            <label htmlFor={id('code')}>Payroll code (optional)</label>
            <input id={id('code')} value={payrollCode} onChange={(e) => setPayrollCode(e.target.value)} aria-describedby={id('code-hint')} maxLength={80} readOnly={lockedCategory} disabled={grouped} />
            <p id={id('code-hint')} className="jr-hint">A tax type code such as FIT or SS, to map that one item differently.</p>
          </div>
          <div className="jr-field">
            <label htmlFor={id('effectiveFrom')}>Effective from <span aria-hidden="true" className="jr-req">*</span></label>
            <input id={id('effectiveFrom')} type="date" value={effectiveFrom} onChange={(e) => setTypedDate(e.target.value)} required {...invalid('effectiveFrom')} />
            <p className="jr-hint">A payroll uses the mapping in effect on its accounting date.</p>
            {err('effectiveFrom')}
          </div>
        </div>

        {grouped && prefill?.groupDimension ? (
          <p className="ax-fieldset" data-testid="mapping-group-scope">
            <strong>Applies to:</strong> {DIMENSION_LABEL[prefill.groupDimension]} group <strong>{prefill.groupLabel ?? prefill.groupKey}</strong>. Every employee in this group is posted to the account you choose, unless an employee-specific override exists.
          </p>
        ) : lockedCategory ? null : (
        <fieldset className="ax-fieldset">
          <legend>Applies to (optional: leave empty for the whole company)</legend>
          <div className="jr-grid">
            {employees && (
              <div className="jr-field">
                <label htmlFor={id('employee')}>Only this employee</label>
                <select id={id('employee')} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                  <option value="">Everyone</option>
                  {employees.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            )}
            {departments && (
              <div className="jr-field">
                <label htmlFor={id('department')}>Only this department</label>
                <select id={id('department')} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">All departments</option>
                  {departments.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div className="jr-field">
              <label htmlFor={id('location')}>Only this location code</label>
              <input id={id('location')} value={locationCode} onChange={(e) => setLocationCode(e.target.value)} aria-describedby={id('location-hint')} maxLength={20} />
              <p id={id('location-hint')} className="jr-hint">The state or location code the payroll carries, for example NY.</p>
            </div>
          </div>
          {(!employees || !departments) && (
            <p className="jr-hint">Employee or department overrides are not offered because the employee list is not available to you.</p>
          )}
        </fieldset>
        )}

        <fieldset className="jr-choice">
          <legend>Treatment</legend>
          <label><input type="radio" name={id('treatment')} checked={treatment === 'Post'} onChange={() => setTreatment('Post')} /> Post to an account</label>
          <label><input type="radio" name={id('treatment')} checked={treatment === 'NonPosting'} onChange={() => setTreatment('NonPosting')} /> Do not post (non-posting, with a reason)</label>
        </fieldset>

        {treatment === 'Post' ? (
          <div className="jr-field">
            <label htmlFor={id('account')}>Account <span aria-hidden="true" className="jr-req">*</span></label>
            <select id={id('account')} value={accountId} onChange={(e) => { setAccountId(e.target.value); setConfirmFunding(false); }} required {...(lockedCategory ? { 'data-autofocus': true } : {})} {...invalid('account')}>
              <option value="">Choose an account</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.accountNumber}: {a.name} ({a.accountType})</option>)}
            </select>
            <p className="jr-hint">Only active accounts from your chart of accounts are listed.</p>
            {err('account')}
            {advice && <p className="jr-warning" role="note">{advice}</p>}
          </div>
        ) : (
          <div className="jr-field">
            <label htmlFor={id('reason')}>Reason this item is not posted <span aria-hidden="true" className="jr-req">*</span></label>
            <textarea id={id('reason')} className="ax-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={REASON_MAX} required aria-describedby={id('reason-hint')} {...invalid('reason')} />
            <p id={id('reason-hint')} className="jr-hint">
              Excluded items are listed in every preview. A non-posting rule is only valid when the item that offsets it is non-posting too, otherwise the entry would not balance.
            </p>
            {err('reason')}
          </div>
        )}

        {needsFundingConfirmation && (
          <div className="jr-field">
            <label className="ax-check">
              <input id={id('funding')} type="checkbox" checked={confirmFunding} onChange={(e) => setConfirmFunding(e.target.checked)} {...invalid('funding')} />
              <span>I confirm net pay should post to this bank (asset) account. It changes how payroll funding is represented.</span>
            </label>
            {err('funding')}
          </div>
        )}
      </form>
    </ModalDialog>
  );
}
